import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import VerdictPanel from "@/components/VerdictPanel";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

export default function AutorisationDetail() {
  const { id } = useParams();
  const { labels, permisMeta, zoneName } = useApp();
  const { user } = useAuth();
  const [a, setA] = useState(null);

  const load = useCallback(() => { api.get(`/autorisations/${id}`).then((r) => setA(r.data)); }, [id]);
  useEffect(load, [load]);
  if (!a) return <div className="mono">Chargement…</div>;

  const ev = a.evaluation;
  const canValidate = ["HSE", "VALIDEUR", "ADMINISTRATEUR"].includes(user?.role);

  const setCtl = async (pi, ci, val) => { await api.post(`/autorisations/${id}/controle`, { pi, ci, val }); load(); };
  const validerPermis = async (pi) => { try { await api.post(`/autorisations/${id}/valider-permis`, { pi }); toast.success("Contrôles validés"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const setMesures = async (val) => { await api.post(`/autorisations/${id}/mesures`, { val }); toast.success(val ? "Mesures validées" : "Mesures retirées"); load(); };
  const statut = async (st, txt) => { try { await api.post(`/autorisations/${id}/statut`, { statut: st, txt }); toast.success("Statut mis à jour"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <div className="crumb"><Link to="/autorisations">Autorisations</Link> / {a.id}</div>
      <h1 style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>{a.id} <StatusBadge value={a.statut} label={labels.statutsAt[a.statut]} /></h1>

      <div className="grid2" style={{ gridTemplateColumns: "1.2fr 1fr", alignItems: "start", marginTop: 16 }}>
        <div>
          <div className="card">
            <table className="kv"><tbody>
              <tr><td>Entreprise</td><td><b>{a.entNom}</b></td></tr>
              <tr><td>Zone</td><td>{zoneName(a.zone)}</td></tr>
              <tr><td>Période</td><td className="mono">{a.date} · {a.deb}–{a.fin}</td></tr>
              <tr><td>Travaux</td><td>{a.typesTravail.map((t) => labels.travaux[t]).join(", ")}</td></tr>
              <tr><td>Description</td><td>{a.desc || "—"}</td></tr>
              <tr><td>Surveillant ext.</td><td>{a.surveillant || "—"}</td></tr>
              {a.operationId && <tr><td>Opération</td><td><Link to={`/operations/${a.operationId}`} className="mono">{a.operationId}</Link></td></tr>}
            </tbody></table>
          </div>

          {(a.permis || []).map((p, pi) => (
            <div className="card" key={pi} data-testid={`permis-${p.type}`}>
              <h3 style={{ fontSize: "1rem", marginBottom: 6, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {permisMeta[p.type]?.nom || p.type}
                {p.valide ? <StatusBadge value="valide" label="Contrôles validés" /> : <StatusBadge value="soumis" label="Contrôles à réaliser" />}
              </h3>
              <div style={{ fontFamily: "'Space Mono'", fontSize: ".6rem", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>
                Responsable : {p.responsable} · Validité {p.validite_h}h · Contrôles avant intervention
              </div>
              {p.controles.map((c, ci) => (
                <label className="ck" key={ci}>
                  <input type="checkbox" checked={c.ok} disabled={p.valide} data-testid={`ctl-${p.type}-${ci}`} onChange={(e) => setCtl(pi, ci, e.target.checked)} />
                  <span>{c.c}</span>
                </label>
              ))}
              {!p.valide && (
                <div style={{ marginTop: 12 }}>
                  <button className="btn btn-v" data-testid={`valider-permis-${p.type}`} disabled={!p.controles.every((c) => c.ok) || !canValidate} onClick={() => validerPermis(pi)}>
                    Valider les contrôles (exploitant de zone)
                  </button>
                  {!canValidate && <span style={{ fontSize: ".72rem", color: "var(--text-muted)", marginLeft: 10 }}>Profil HSE / Valideur requis</span>}
                </div>
              )}
            </div>
          ))}

          {ev.items.some((i) => i.niv !== "req" && i.code !== "COACT" && !i.msg.includes("Conflit")) && (
            <div className="card">
              <h3 style={{ fontSize: "1rem", marginBottom: 6 }}>Mesures compensatoires (risques de zone)</h3>
              <label className="ck"><input type="checkbox" checked={!!a.mesures_validees} data-testid="mesures-toggle" onChange={(e) => setMesures(e.target.checked)} />
                <span>Mesures compensatoires en place et validées par l'exploitant de zone</span></label>
            </div>
          )}

          <div className="card">
            <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>Actions</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["soumis", "envalidation"].includes(a.statut) && <>
                <button className="btn btn-p" data-testid="btn-autoriser" disabled={ev.bloque} onClick={() => statut("autorise", "Autorisée (HSE) — moteur : aucun blocage")}>Autoriser</button>
                <button className="btn btn-r" onClick={() => statut("refuse", "Refusée")}>Refuser</button>
              </>}
              {a.statut === "autorise" && <button className="btn btn-p" data-testid="btn-demarrer" onClick={() => statut("demarre", "Démarrée — briefing intervenants confirmé")}>Démarrer</button>}
              {["autorise", "demarre"].includes(a.statut) && <button className="btn btn-a" onClick={() => statut("suspendu", "Suspendue — conditions dégradées")}>Suspendre</button>}
              {a.statut === "suspendu" && <button className="btn btn-p" disabled={ev.bloque} onClick={() => statut("demarre", "Reprise après revalidation")}>Reprendre</button>}
              {a.statut === "demarre" && <button className="btn btn-g" onClick={() => statut("cloture", "Clôturée — contrôles de fin réalisés")}>Clôturer</button>}
            </div>
            {ev.bloque && ["soumis", "envalidation", "suspendu"].includes(a.statut) &&
              <div className="notice" style={{ margin: "12px 0 0" }}>La transition vers « Autorisé » est bloquée par le moteur de règles — voir le panneau de droite.</div>}
          </div>

          <div className="card">
            <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>Journal (traçabilité)</h3>
            <ul className="journal">{(a.journal || []).slice().reverse().map((j, i) => <li key={i}><span className="h">{j.h}</span>{j.t}</li>)}</ul>
          </div>
        </div>

        <VerdictPanel evaluation={ev} title={ev.bloque ? "Bloqué" : (ev.items.some((i) => i.niv === "warn") ? "Réserves actives" : "Conforme")} />
      </div>
    </div>
  );
}
